import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, X, MapPin, Clock, Ticket, ArrowSquareOut, User, HourglassMedium } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { homeFor } from "../lib/home";
import { FavouriteStar } from "../components/FavouriteStar";
import { RatingBadge, Stars } from "../components/ClassReview";

const SYM = { usd: "$", gbp: "£", eur: "€", cad: "$", aud: "$", nzd: "$", inr: "₹", jpy: "¥" };
const money = (n, c) => `${SYM[(c || "gbp").toLowerCase()] || ""}${Number(n || 0).toFixed(2)}`;

const dayLabel = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  } catch (_e) { return iso; }
};
const timeLabel = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch (_e) { return ""; }
};

export default function PublicGroupSchedule() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [waitlisting, setWaitlisting] = useState(null);
  const [buyingPack, setBuyingPack] = useState(null);
  const [gate, setGate] = useState(null); // "book" | "waitlist" | "pack" when an account is needed
  const { user } = useAuth();
  const me = user && user.role === "client" ? user : null;
  const needAccount = (kind, open) => (item) => { if (me) open(item); else setGate(kind); };

  const load = async () => {
    try {
      const { data: resp } = await api.get(`/public/group/${slug}`);
      setData(resp);
    } catch (err) {
      if (err.response?.status !== 404) toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);
  const [paidResult, setPaidResult] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const bs = params.get("booking_session"), ps = params.get("pack_session");
    if (!bs && !ps) return;
    window.history.replaceState({}, "", window.location.pathname);
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(bs ? `/public/group/${slug}/booking-status/${bs}` : `/public/group/${slug}/pack-status/${ps}`);
        if (data.status === "paid") { setPaidResult({ kind: bs ? "booking" : "pack", ...data }); load(); return; }
      } catch (_e) { /* keep polling */ }
      if (tries < 8) setTimeout(poll, 2000); else setPaidResult({ kind: bs ? "booking" : "pack", status: "pending" });
    };
    poll();
  }, [slug]);

  const grouped = useMemo(() => {
    if (!data?.classes) return [];
    const map = new Map();
    for (const c of data.classes) {
      const key = (c.start || "").slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return [...map.entries()];
  }, [data]);

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center"><p className="tracker text-zinc-500 text-[11px]">Loading…</p></div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col items-center justify-center gap-4" data-testid="group-schedule-not-found">
        <h1 className="font-display text-4xl">Schedule not found</h1>
        <p className="text-zinc-500">This booking page is no longer available.</p>
        <Link to="/" className="tracker text-brand-light text-[11px] underline">Back home</Link>
      </div>
    );
  }

  const accent = data.brand_accent_color || "#2E5BA8";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="public-group-schedule" style={{ "--brand-accent": accent }}>
      <header className="border-b border-zinc-900">
        <div className="max-w-4xl mx-auto px-6 py-12 sm:py-16 flex flex-col items-start gap-5">
          <div className="flex items-center gap-3">
            {data.brand_logo_b64 ? (
              <img src={data.brand_logo_b64} alt={data.brand_name} className="h-12 w-12 object-contain" />
            ) : (
              <div className="h-12 w-12 flex items-center justify-center font-display text-2xl" style={{ backgroundColor: accent }}>
                <span className="text-zinc-950">{(data.brand_name || "T").charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div>
              <p className="tracker text-zinc-500 text-[10px]">[ GROUP SESSIONS ]</p>
              <p className="font-display text-2xl tracking-tight" data-testid="group-schedule-brand">{data.brand_name}</p>
              <RatingBadge rating={data.rating} testId="group-schedule-rating" />
            </div>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-[0.95] tracking-tight">
            Book a <span style={{ color: accent }}>class</span>.
          </h1>
          <p className="text-zinc-400">Grab a spot in an upcoming group session. Booking takes a free account — so your spot, credits and reminders are always yours.</p>
          {me ? (
            <p className="tracker text-[10px] text-zinc-500" data-testid="group-signed-in-as">SIGNED IN AS <span className="text-zinc-200">{me.name}</span> · {me.email} · <Link to={homeFor(me)} className="underline" style={{ color: accent }}>My classes</Link>
              <span className="inline-flex items-center gap-1 ml-3 align-middle"><FavouriteStar trainerId={data.trainer_id} brandName={data.brand_name} size={14} accent={accent} /> <span className="text-zinc-500">STAR FOR NEW-CLASS &amp; OPEN-SPOT EMAILS</span></span></p>
          ) : (
            <div className="flex gap-2 flex-wrap" data-testid="group-account-cta">
              <Link to={`/register?as=member&next=${encodeURIComponent(`/g/${slug}`)}`} className="inline-flex items-center gap-2 px-5 py-2.5 font-display text-sm text-zinc-950" style={{ backgroundColor: accent }} data-testid="group-create-account">Create free account <ArrowRight size={14} weight="bold" /></Link>
              <Link to={`/login?next=${encodeURIComponent(`/g/${slug}`)}`} className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 font-display text-sm hover:border-zinc-500" data-testid="group-sign-in">Sign in</Link>
            </div>
          )}
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 py-12">
        {grouped.length === 0 ? (
          <div className="border border-zinc-800 p-12 text-center" data-testid="group-schedule-empty">
            <p className="font-display text-2xl mb-2">No upcoming classes.</p>
            <p className="text-zinc-500 text-sm">Check back soon — new sessions are added regularly.</p>
          </div>
        ) : (
          <div className="space-y-10" data-testid="group-schedule-list">
            {grouped.map(([day, classes]) => (
              <div key={day}>
                <h2 className="tracker text-zinc-500 text-[10px] mb-3">{dayLabel(classes[0].start).toUpperCase()}</h2>
                <div className="space-y-3">
                  {classes.map((c) => (
                    <article key={c.id} className="border border-zinc-800 bg-zinc-950 p-5 flex items-center justify-between gap-4 flex-wrap hover:border-zinc-700 transition-colors" data-testid={`group-public-class-${c.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-display text-xl" style={{ color: accent }}>{timeLabel(c.start)}</span>
                          <h3 className="font-display text-xl">{c.title}</h3>
                          {c.instructor_name && (
                            <span className="text-zinc-400 text-sm inline-flex items-center gap-1" data-testid={`group-public-instructor-${c.id}`}>
                              <User size={12} /> with {c.instructor_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1.5 text-zinc-500 text-xs flex-wrap">
                          <span className="inline-flex items-center gap-1"><Clock size={12} /> {c.duration_min} min</span>
                          {c.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {c.location}</span>}
                          <span className="money text-zinc-300">{money(c.price, c.currency)}</span>
                        </div>
                        {c.description && <p className="text-zinc-400 text-sm mt-2 line-clamp-2">{c.description}</p>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className={`tracker text-[9px] px-2 py-1 border ${c.spots_left === 0 ? "border-red-500/40 text-red-400" : "border-zinc-700 text-zinc-400"}`} data-testid={`group-public-spots-${c.id}`}>
                          {c.spots_left === 0 ? `FULL${c.waitlist_count ? ` · ${c.waitlist_count} WAITING` : ""}` : `${c.spots_left} SPOT${c.spots_left === 1 ? "" : "S"} LEFT`}
                        </span>
                        {c.spots_left === 0 ? (
                          <button
                            onClick={() => needAccount("waitlist", setWaitlisting)(c)}
                            className="inline-flex items-center gap-2 px-5 py-2.5 font-display text-xs tracking-wide border hover:bg-zinc-900 transition-colors"
                            style={{ borderColor: accent, color: accent }}
                            data-testid={`group-public-waitlist-${c.id}`}
                          >
                            Join waitlist <HourglassMedium size={14} weight="bold" />
                          </button>
                        ) : (
                        <button
                          onClick={() => needAccount("book", setBooking)(c)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 font-display text-xs tracking-wide text-zinc-950 hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: accent }}
                          data-testid={`group-public-book-${c.id}`}
                        >
                          Book <ArrowRight size={14} weight="bold" />
                        </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {data.packs?.length > 0 && (
        <section className="border-t border-zinc-900" data-testid="group-packs-section">
          <div className="max-w-4xl mx-auto px-6 py-12">
            <p className="tracker text-zinc-500 text-[10px] mb-2">[ CLASS PACKS ]</p>
            <h2 className="font-display text-3xl mb-2">Save with a pack.</h2>
            <p className="text-zinc-400 text-sm mb-8">Buy a bundle of class credits — you'll get a code by email to use at booking.</p>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.packs.map((p) => (
                <article key={p.id} className="border border-zinc-800 p-6 flex flex-col gap-3" data-testid={`group-public-pack-${p.id}`}>
                  <Ticket size={22} weight="duotone" style={{ color: accent }} />
                  <h3 className="font-display text-xl">{p.name}</h3>
                  <p className="text-zinc-500 text-sm">{p.credits} class credits</p>
                  <div className="font-display text-3xl" style={{ color: accent }}>{money(p.price, p.currency)}</div>
                  <button
                    onClick={() => needAccount("pack", setBuyingPack)(p)}
                    className="mt-auto inline-flex items-center justify-center gap-2 border px-4 py-2.5 font-display text-xs tracking-wide hover:bg-zinc-900 transition-colors"
                    style={{ borderColor: accent }}
                    data-testid={`group-public-buy-pack-${p.id}`}
                  >
                    Buy pack <ArrowRight size={14} weight="bold" />
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.reviews?.length > 0 && (
        <section className="border-t border-zinc-900" data-testid="group-reviews-section">
          <div className="max-w-4xl mx-auto px-6 py-12">
            <p className="tracker text-zinc-500 text-[10px] mb-2">[ WHAT PEOPLE SAY ]</p>
            <h2 className="font-display text-3xl mb-1">From recent classes.</h2>
            {data.rating && <p className="text-zinc-400 text-sm mb-6"><RatingBadge rating={data.rating} size={14} /> average across {data.rating.count} reviews</p>}
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              {data.reviews.map((r, i) => (
                <blockquote key={i} className="border border-zinc-800 p-5" data-testid={`group-review-${i}`}>
                  <Stars value={r.rating} />
                  <p className="text-sm text-zinc-200 mt-3">“{r.comment}”</p>
                  <footer className="tracker text-[9px] text-zinc-500 mt-3">{r.name} · {r.class_title}</footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="max-w-4xl mx-auto px-6 py-8 text-center">
        <p className="tracker text-zinc-600 text-[9px]">Powered by <Link to="/" className="text-zinc-400 hover:text-zinc-200">Traina</Link></p>
      </footer>

      {gate && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setGate(null)}>
          <div className="bg-zinc-950 border border-zinc-800 w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="group-account-gate">
            <p className="tracker text-zinc-500 text-[10px] mb-2">[ {gate === "pack" ? "BUY A PACK" : gate === "waitlist" ? "JOIN THE WAITLIST" : "BOOK A SPOT"} ]</p>
            <h2 className="font-display text-3xl mb-3">{user ? "Switch to a member account." : "Create a free account to book."}</h2>
            <p className="text-zinc-400 text-sm mb-6">
              {user
                ? "You're signed in to a business or instructor account. Class bookings are made from a personal member account — sign out and create one (30 seconds)."
                : "Your bookings, class credits and reminders live in one place — and the business can reach you if anything changes. If you're moving over from another booking app, use the same email so your credits follow you."}
            </p>
            {!user && (
              <div className="space-y-2">
                <Link to={`/register?as=member&next=${encodeURIComponent(`/g/${slug}`)}`} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 font-display text-sm text-zinc-950" style={{ backgroundColor: accent }} data-testid="group-gate-register">Create free account <ArrowRight size={16} weight="bold" /></Link>
                <Link to={`/login?next=${encodeURIComponent(`/g/${slug}`)}`} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-zinc-700 font-display text-sm hover:border-zinc-500" data-testid="group-gate-login">I already have an account</Link>
              </div>
            )}
            <button onClick={() => setGate(null)} className="block mx-auto mt-6 tracker text-zinc-400 text-[10px] underline" data-testid="group-gate-close">Not now</button>
          </div>
        </div>
      )}
      {booking && <BookModal cls={booking} slug={slug} accent={accent} me={me} onClose={() => setBooking(null)} onBooked={load} />}
      {waitlisting && <WaitlistModal cls={waitlisting} slug={slug} accent={accent} me={me} onClose={() => setWaitlisting(null)} onJoined={load} />}
      {paidResult && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPaidResult(null)}>
          <div className="bg-zinc-950 border border-zinc-800 w-full max-w-md p-8 text-center" onClick={(e) => e.stopPropagation()} data-testid="group-payment-result">
            {paidResult.status === "paid" ? (
              <>
                <p className="tracker text-zinc-500 text-[10px] mb-2">[ PAYMENT CONFIRMED ]</p>
                <p className="font-display text-3xl mb-3" style={{ color: accent }}>{paidResult.kind === "booking" ? "You're in!" : "Pack unlocked!"}</p>
                {paidResult.kind === "booking" ? (
                  <p className="text-zinc-400 text-sm mb-6">Your spot in <strong className="text-white">{paidResult.class_title}</strong> is confirmed — check your inbox for the details.</p>
                ) : (
                  <>
                    <p className="text-zinc-400 text-sm mb-3">{paidResult.pack_name} · {paidResult.credits} credits. Your code (also emailed):</p>
                    <p className="font-mono text-2xl mb-6" style={{ color: accent }} data-testid="group-pack-code">{paidResult.code}</p>
                  </>
                )}
              </>
            ) : (
              <>
                <p className="font-display text-2xl mb-3">Payment is processing…</p>
                <p className="text-zinc-400 text-sm mb-6">We'll email you the moment it lands. If you cancelled at checkout, your hold will be released shortly.</p>
              </>
            )}
            <button onClick={() => setPaidResult(null)} className="tracker text-zinc-400 text-[10px] underline" data-testid="group-payment-result-close">Close</button>
          </div>
        </div>
      )}
      {buyingPack && <PackBuyModal pack={buyingPack} slug={slug} accent={accent} me={me} onClose={() => setBuyingPack(null)} />}
    </div>
  );
}

function Booker({ me, accent }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/60 px-4 py-2.5 text-xs flex items-center justify-between gap-3" data-testid="group-booking-as">
      <span className="text-zinc-400">Booking as <strong className="text-zinc-100">{me.name}</strong> · {me.email}</span>
      <Link to={homeFor(me) + "/profile"} className="tracker text-[9px] underline shrink-0" style={{ color: accent }}>Edit</Link>
    </div>
  );
}

function BookModal({ cls, slug, accent, me, onClose, onBooked }) {
  const [form, setForm] = useState({ name: me.name || "", email: me.email || "", phone: me.phone || "" });
  const [method, setMethod] = useState("stripe");
  const [packCode, setPackCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [credits, setCredits] = useState(0);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get(`/public/group/${slug}/credits`).then(({ data }) => setCredits(data.credits || 0)).catch(() => setCredits(0));
  }, [slug]);
  const hasCredits = credits > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!hasCredits && method === "pack" && !packCode.trim()) return toast.error("Enter your pack code");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/group/${slug}/book`, {
        class_id: cls.id,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        method: !hasCredits && method === "pack" ? "pack" : "stripe",
        pack_code: !hasCredits && method === "pack" ? packCode.trim() : null,
        origin_url: window.location.origin,
      });
      if (data.pay_url) { window.location.href = data.pay_url; return; }
      setResult(data);
      onBooked();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 w-full max-w-md p-8 my-8" onClick={(e) => e.stopPropagation()} data-testid="group-book-modal">
        {result ? (
          <div className="text-center py-4" data-testid="group-book-success">
            <p className="font-display text-3xl mb-3" style={{ color: accent }}>{result.status === "paid" ? "You're in!" : "Spot held!"}</p>
            <p className="text-zinc-400 text-sm mb-6">{result.note}</p>
            {result.pay_url && (
              <a href={result.pay_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 font-display text-sm tracking-wide text-zinc-950 mb-4"
                style={{ backgroundColor: accent }} data-testid="group-book-pay-now">
                Pay now <ArrowSquareOut size={16} weight="bold" />
              </a>
            )}
            <p className="tracker text-zinc-600 text-[9px]">A confirmation email is on its way.</p>
            <button onClick={onClose} className="mt-6 tracker text-zinc-400 text-[10px] underline" data-testid="group-book-close">Close</button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="tracker text-zinc-500 text-[10px] mb-1">[ BOOK A SPOT ]</p>
                <h2 className="font-display text-2xl">{cls.title}</h2>
                <p className="tracker text-zinc-500 text-[10px] mt-1">{dayLabel(cls.start)} · {timeLabel(cls.start)} · {money(cls.price, cls.currency)}</p>
              </div>
              <button onClick={onClose}><X size={20} weight="bold" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Booker me={me} accent={accent} />
              <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-600" data-testid="group-book-phone" />

              {hasCredits ? (
                <div className="border px-4 py-3 text-sm flex items-center gap-3" style={{ borderColor: accent }} data-testid="group-book-credits-notice">
                  <Ticket size={18} weight="duotone" style={{ color: accent }} className="shrink-0" />
                  <span>You have <strong style={{ color: accent }}>{credits} class credit{credits === 1 ? "" : "s"}</strong> on file — this booking uses 1, nothing to pay.</span>
                </div>
              ) : (
              <div className="space-y-2">
                <p className="tracker text-zinc-500 text-[10px]">HOW WOULD YOU LIKE TO PAY? <span className="opacity-60">· paid upfront</span></p>
                <label className={`flex items-center gap-3 border px-4 py-3 cursor-pointer ${method === "stripe" ? "border-zinc-500" : "border-zinc-800"}`}>
                  <input type="radio" name="pay" checked={method === "stripe"} onChange={() => setMethod("stripe")} data-testid="group-pay-stripe" />
                  <span className="text-sm">Pay by card now (secure Stripe checkout)</span>
                </label>
                <label className={`flex items-center gap-3 border px-4 py-3 cursor-pointer ${method === "pack" ? "border-zinc-500" : "border-zinc-800"}`}>
                  <input type="radio" name="pay" checked={method === "pack"} onChange={() => setMethod("pack")} data-testid="group-pay-pack" />
                  <span className="text-sm">Use a class pack code</span>
                </label>
                {method === "pack" && (
                  <input placeholder="PK-XXXXXX" value={packCode} onChange={(e) => setPackCode(e.target.value.toUpperCase())} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-zinc-600" data-testid="group-book-pack-code" />
                )}
              </div>
              )}

              <button type="submit" disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 font-display text-sm tracking-wide text-zinc-950 hover:opacity-90 disabled:opacity-40"
                style={{ backgroundColor: accent }} data-testid="group-book-submit">
                {submitting ? "Booking…" : <>{hasCredits ? "Book with 1 credit" : "Book my spot"} <ArrowRight size={16} weight="bold" /></>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function WaitlistModal({ cls, slug, accent, me, onClose, onJoined }) {
  const [form, setForm] = useState({ name: me.name || "", email: me.email || "", phone: me.phone || "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/group/${slug}/waitlist`, {
        class_id: cls.id, name: form.name.trim(), email: form.email.trim().toLowerCase(), phone: form.phone.trim(),
      });
      setResult(data);
      onJoined();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not join the waitlist");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 w-full max-w-md p-8 my-8" onClick={(e) => e.stopPropagation()} data-testid="group-waitlist-modal">
        {result ? (
          <div className="text-center py-4" data-testid="group-waitlist-success">
            <HourglassMedium size={36} weight="duotone" style={{ color: accent }} className="mx-auto mb-3" />
            <p className="font-display text-3xl mb-3">You're #{result.position} in line.</p>
            <p className="text-zinc-400 text-sm mb-6">The moment a spot frees up you'll be booked in automatically and emailed your confirmation — first come, first served.</p>
            <button onClick={onClose} className="tracker text-zinc-400 text-[10px] underline" data-testid="group-waitlist-close">Close</button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="tracker text-zinc-500 text-[10px] mb-1">[ JOIN THE WAITLIST ]</p>
                <h2 className="font-display text-2xl">{cls.title}</h2>
                <p className="tracker text-zinc-500 text-[10px] mt-1">{dayLabel(cls.start)} · {timeLabel(cls.start)} · full{cls.waitlist_count ? ` · ${cls.waitlist_count} waiting` : ""}</p>
              </div>
              <button onClick={onClose}><X size={20} weight="bold" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Booker me={me} accent={accent} />
              <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-600" data-testid="group-waitlist-phone" />
              <p className="text-zinc-500 text-xs">If someone drops out we hold the spot for you and email a secure pay link — it's yours once paid.</p>
              <button type="submit" disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 font-display text-sm tracking-wide text-zinc-950 hover:opacity-90 disabled:opacity-40"
                style={{ backgroundColor: accent }} data-testid="group-waitlist-submit">
                {submitting ? "Joining…" : <>Put me on the list <ArrowRight size={16} weight="bold" /></>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function PackBuyModal({ pack, slug, accent, me, onClose }) {
  const form = { name: me.name || "", email: me.email || "" };
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/group/${slug}/packs/${pack.id}/buy`, {
        name: form.name.trim(), email: form.email.trim().toLowerCase(), origin_url: window.location.origin,
      });
      if (data.pay_url) { window.location.href = data.pay_url; return; }
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start purchase");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="group-pack-buy-modal">
        {result ? (
          <div className="text-center py-4" data-testid="group-pack-buy-success">
            <p className="font-display text-3xl mb-3" style={{ color: accent }}>Almost there!</p>
            <p className="text-zinc-400 text-sm mb-4">{result.note}</p>
            {result.pay_url ? (
              <a href={result.pay_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 font-display text-sm tracking-wide text-zinc-950"
                style={{ backgroundColor: accent }} data-testid="group-pack-pay-now">
                Complete payment <ArrowSquareOut size={16} weight="bold" />
              </a>
            ) : (
              <p className="tracker text-amber-400 text-[10px]">The coach will send payment details by email.</p>
            )}
            <button onClick={onClose} className="block mx-auto mt-6 tracker text-zinc-400 text-[10px] underline" data-testid="group-pack-buy-close">Close</button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="tracker text-zinc-500 text-[10px] mb-1">[ BUY PACK ]</p>
                <h2 className="font-display text-2xl">{pack.name}</h2>
                <p className="tracker text-zinc-500 text-[10px] mt-1">{pack.credits} credits · {money(pack.price, pack.currency)}</p>
              </div>
              <button onClick={onClose}><X size={20} weight="bold" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Booker me={me} accent={accent} />
              <p className="text-zinc-500 text-xs">Credits are added to your account the moment payment lands — they're used automatically when you book.</p>
              <button type="submit" disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 font-display text-sm tracking-wide text-zinc-950 hover:opacity-90 disabled:opacity-40"
                style={{ backgroundColor: accent }} data-testid="group-pack-buy-submit">
                {submitting ? "Starting…" : <>Continue to payment <ArrowRight size={16} weight="bold" /></>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
